import { useCallback } from 'react';

import { extractEventHandlers, isFocusVisible, visuallyHidden } from '../../../../../utils/dom';
import { areValuesEqual, clamp, constrainRangeValue, findClosest, focusThumb } from '../utils';

import type * as React from 'react';

import type { GetHiddenInputProps, Mark, SliderValue, UseSliderHiddenInputProps } from '../types';

interface UseSliderHiddenInputPropsParameters {
  ariaLabelledby?: string;
  disabled: boolean;
  disableSwap: boolean;
  handleChange?: (event: Event | React.SyntheticEvent, value: SliderValue, activeThumb: number) => void;
  isRtl: boolean;
  marks: Mark[];
  marksValues: number[];
  max: number;
  min: number;
  minDistance: number;
  name?: string;
  onChangeCommitted?: (event: Event | React.SyntheticEvent, value: SliderValue) => void;
  orientation: 'horizontal' | 'vertical';
  range: boolean;
  readOnly: boolean;
  resolveKeyboardValue?: (currentValue: number, nextValue: number) => number;
  scale: (value: number) => number;
  setFocusedThumbIndex: (index: number) => void;
  setOpen: (index: number) => void;
  setValueState: (value: SliderValue) => void;
  shiftStep: number;
  sliderRef: React.RefObject<HTMLSpanElement | null>;
  step: null | number;
  tabIndex?: number;
  valueDerived: SliderValue;
  values: number[];
}

/**
 * Fires the change callback only when the new value differs from the previous one.
 * Prevents redundant updates when a keyboard/input action produces the same value
 * (e.g. pressing an arrow key when already at min/max).
 */
const emitIfChanged = (
  emitChange: UseSliderHiddenInputPropsParameters['handleChange'],
  event: Event | React.SyntheticEvent,
  newValue: SliderValue,
  activeThumb: number,
  previousValue: SliderValue,
): void => {
  if (emitChange && !areValuesEqual(newValue, previousValue)) {
    emitChange(event, newValue, activeThumb);
  }
};

/**
 * Builds the props getter for the hidden `<input type="range">` elements that
 * provide native keyboard accessibility for each slider thumb.
 *
 * Each thumb renders a visually hidden range input. This hook handles:
 * - **Arrow key navigation** with `step` and `shiftStep` increments.
 * - **Page Up/Down** for large jumps.
 * - **Mark-only mode** (`step === null`) — arrow keys jump between marks.
 * - **RTL support** — reverses left/right arrow directions.
 * - **Range constraints** — enforces `minDistance` and `disableSwap` on keyboard moves.
 * - **Focus tracking** — updates `focusedThumbIndex` and `open` state for value labels.
 *
 * @returns A `getHiddenInputProps` function that merges external props with the
 *          generated accessibility and event handler props.
 */
const useSliderHiddenInputProps = ({
  ariaLabelledby,
  disabled,
  disableSwap,
  handleChange,
  isRtl,
  marks,
  marksValues,
  max,
  min,
  minDistance,
  name,
  onChangeCommitted,
  orientation,
  range,
  readOnly,
  resolveKeyboardValue,
  scale,
  setFocusedThumbIndex,
  setOpen,
  setValueState,
  shiftStep,
  sliderRef,
  step,
  tabIndex,
  valueDerived,
  values,
}: UseSliderHiddenInputPropsParameters): GetHiddenInputProps => {
  const getArrowStepDirection = useCallback(
    (key: string): -1 | 0 | 1 => {
      switch (key) {
        case 'ArrowDown': {
          return -1;
        }
        case 'ArrowLeft': {
          return isRtl ? 1 : -1;
        }
        case 'ArrowRight': {
          return isRtl ? -1 : 1;
        }
        case 'ArrowUp': {
          return 1;
        }
        default: {
          return 0;
        }
      }
    },
    [isRtl],
  );

  /**
   * Core value-change handler shared by keyboard events and native range input changes.
   * Resolves the proposed `nextValue` into a valid slider position, applies range
   * constraints, updates state, and fires callbacks.
   */
  const changeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, nextValue: number) => {
      if (readOnly) {
        return;
      }

      const index = Number(event.currentTarget.dataset.index);
      const value = values[index];
      const marksIndex = marksValues.indexOf(value);
      const currentMarkIndex = marksIndex === -1 ? findClosest(marksValues, value ?? min) : marksIndex;
      let newValue = nextValue;

      // In marks-only mode (step === null), arrow keys jump between mark positions
      // instead of moving by a fixed increment.
      if (marks.length > 0 && step == null) {
        const minMarksValue = marksValues[0] ?? min;
        const maxMarksValue = marksValues.at(-1) ?? max;
        const exactMarkValue = marksValues.find((markValue) => markValue === newValue);

        if (exactMarkValue != null) {
          newValue = exactMarkValue;
        } else if (newValue >= maxMarksValue) {
          newValue = maxMarksValue;
        } else if (newValue <= minMarksValue) {
          newValue = minMarksValue;
        } else {
          // Move to the adjacent mark in the direction of travel
          const adjacentIndex = newValue < value ? currentMarkIndex - 1 : currentMarkIndex + 1;
          newValue = marksValues[Math.min(Math.max(adjacentIndex, 0), marksValues.length - 1)] ?? value;
        }
      }

      newValue = clamp(newValue, min, max);
      let outputValue: SliderValue = newValue;
      let activeIndex = index;

      if (range) {
        // Apply range constraints (minDistance, disableSwap) and re-focus
        // the correct thumb if it swapped position.
        const constrainedRange = constrainRangeValue(values, newValue, index, {
          disableSwap,
          max,
          min,
          minDistance,
        });

        outputValue = constrainedRange.value as SliderValue;
        activeIndex = constrainedRange.activeIndex;

        focusThumb({ activeIndex, sliderRef });
      }

      setValueState(outputValue);
      setFocusedThumbIndex(index);

      emitIfChanged(handleChange, event, outputValue, activeIndex, valueDerived ?? min);
      onChangeCommitted?.(event, outputValue);
    },
    [
      disableSwap,
      handleChange,
      marks,
      marksValues,
      max,
      min,
      minDistance,
      onChangeCommitted,
      range,
      readOnly,
      setFocusedThumbIndex,
      setValueState,
      sliderRef,
      step,
      valueDerived,
      values,
    ],
  );

  const getHiddenInputProps: GetHiddenInputProps = useCallback(
    <ExternalProps extends Record<string, unknown> = Record<string, never>>(
      externalProps: ExternalProps = {} as ExternalProps,
    ): UseSliderHiddenInputProps<ExternalProps> => {
      const otherHandlers = extractEventHandlers(externalProps);
      const rawIndex = Number((externalProps as { 'data-index'?: number | string })['data-index']);
      const resolvedName = range && name && Number.isFinite(rawIndex) ? `${name}[${rawIndex}]` : name;
      const marksOnlyNativeStep =
        step === null && marksValues.length > 1
          ? marksValues.slice(1).reduce((smallestGap, markValue, index) => {
              const previousMarkValue = marksValues[index];

              if (previousMarkValue == null) {
                return smallestGap;
              }

              return Math.min(smallestGap, markValue - previousMarkValue);
            }, Number.POSITIVE_INFINITY)
          : undefined;

      const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
        const index = Number(event.currentTarget.dataset.index);

        if (isFocusVisible(event.target)) {
          setFocusedThumbIndex(index);
        }

        setOpen(index);
        otherHandlers.onFocus?.(event);
      };

      const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        if (!isFocusVisible(event.target)) {
          setFocusedThumbIndex(-1);
        }

        setOpen(-1);
        otherHandlers.onBlur?.(event);
      };

      /**
       * Handles keyboard navigation for the hidden range input.
       * - Arrow keys move by `step` (or `shiftStep` when Shift is held).
       * - PageUp/PageDown move by `shiftStep`.
       * - In marks-only mode (step === null), the native range input handles movement.
       */
      const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        const index = Number(event.currentTarget.dataset.index);
        const value = values[index];
        const arrowDirection = getArrowStepDirection(event.key);
        let newValue: null | number = null;

        if (step === null && marks.length > 0) {
          const minMarksValue = marksValues[0] ?? min;
          const maxMarksValue = marksValues.at(-1) ?? max;

          if (event.key === 'Home') {
            newValue = minMarksValue;
          } else if (event.key === 'End') {
            newValue = maxMarksValue;
          } else if (arrowDirection !== 0) {
            newValue = value + arrowDirection;
          }
        } else if (step !== null) {
          // Shift+Arrow uses the larger shiftStep for coarser control
          const keyboardStep = event.shiftKey ? shiftStep : step;

          switch (event.key) {
            case 'End': {
              newValue = max;

              break;
            }
            case 'Home': {
              newValue = min;

              break;
            }
            case 'PageDown': {
              newValue = Math.max(value - shiftStep, min);

              break;
            }
            case 'PageUp': {
              newValue = Math.min(value + shiftStep, max);

              break;
            }
            default: {
              if (arrowDirection !== 0) {
                newValue = clamp(value + arrowDirection * keyboardStep, min, max);
              }
            }
          }
        }

        if (newValue !== null) {
          // Allow snap-to-marks to override the keyboard value when step mode is active.
          if (step !== null && resolveKeyboardValue) {
            newValue = resolveKeyboardValue(value, newValue);
          }

          changeValue(event, newValue);
          event.preventDefault();
        }

        otherHandlers.onKeyDown?.(event);
      };

      const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        otherHandlers.onChange?.(event);
        changeValue(event, event.target.valueAsNumber);
      };

      return {
        'aria-labelledby': ariaLabelledby,
        'aria-orientation': orientation,
        'aria-valuemax': scale(max),
        'aria-valuemin': scale(min),
        disabled,
        max,
        min,
        name: resolvedName,
        step:
          step === null && marks.length > 0
            ? Number.isFinite(marksOnlyNativeStep)
              ? marksOnlyNativeStep
              : 1
            : (step ?? undefined),
        tabIndex,
        type: 'range',
        ...externalProps,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        onKeyDown: handleKeyDown,
        style: {
          ...visuallyHidden,
          direction: isRtl ? 'rtl' : 'ltr',
          height: '100%',
          width: '100%',
        },
      };
    },
    [
      ariaLabelledby,
      changeValue,
      disabled,
      getArrowStepDirection,
      isRtl,
      marks.length,
      max,
      min,
      orientation,
      resolveKeyboardValue,
      range,
      name,
      scale,
      setFocusedThumbIndex,
      setOpen,
      shiftStep,
      step,
      tabIndex,
      values,
    ],
  );

  return getHiddenInputProps;
};

export default useSliderHiddenInputProps;
